import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { Event } from "../../../Global/Base/Events";
import { IFileStreamReader } from "../../../Global/Base/Streams/IFileStreamReader";
import { FileUtils } from "../../../Global/Base/Utils/FileUtils";
import { OWObject } from "../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { FileUploader } from "../../../OW.Remoting/Remoting/FileUploader";
import { OWManager } from "../../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../../OW.Remoting/Remoting/RequestContext";
import { SerializeFuncs } from "../../../OW.Remoting/Remoting/Serializers";
import { IUploadManager, IUploadManagerInfo, UploadManager } from "./UploadManager";

const logger = LazyLoadingSectionLogger.create(LoggingSection.upload);

export class DefUploadManager implements IUploadManager {
    //#region Fields
    private _onUploadRegistered: Event<UploadManager, IUploadManagerInfo> = new Event<UploadManager, IUploadManagerInfo>();
    private _onUploadStart: Event<UploadManager, IUploadManagerInfo> = new Event<UploadManager, IUploadManagerInfo>();
    private _onUploadProgress: Event<UploadManager, IUploadManagerInfo> = new Event<UploadManager, IUploadManagerInfo>();
    private _onUploadError: Event<UploadManager, IUploadManagerInfo> = new Event<UploadManager, IUploadManagerInfo>();
    private _onUploadCancel: Event<UploadManager, IUploadManagerInfo> = new Event<UploadManager, IUploadManagerInfo>();
    private _onUploadComplete: Event<UploadManager, IUploadManagerInfo> = new Event<UploadManager, IUploadManagerInfo>();

    private _uploadList: Map<string, IUploadManagerInfo> = new Map<string, IUploadManagerInfo>();

    private _removeAfterFinish: boolean = false;
    private _removeAfterError: boolean = false;
    private _removeAfterCancel: boolean = false;
    private _updateInstanceAfterUpload: boolean = false;

    private _uploadStarted: { [type: string]: number } = { _global: 0 };
    private _uploadCompleted: { [type: string]: number } = { _global: 0 };
    //#endregion Fields

    //#region Properties
    public get onUploadRegistered(): Event<UploadManager, IUploadManagerInfo> {
        return this._onUploadRegistered;
    }

    public get onUploadStart(): Event<UploadManager, IUploadManagerInfo> {
        return this._onUploadStart;
    }

    public get onUploadProgress(): Event<UploadManager, IUploadManagerInfo> {
        return this._onUploadProgress;
    }

    public get onUploadError(): Event<UploadManager, IUploadManagerInfo> {
        return this._onUploadError;
    }

    public get onUploadCancel(): Event<UploadManager, IUploadManagerInfo> {
        return this._onUploadCancel;
    }

    public get onUploadComplete(): Event<UploadManager, IUploadManagerInfo> {
        return this._onUploadComplete;
    }

    /**
     * Nastavení zda má být upload ze seznamu odstraněn po jeho dokončení
     */
    public get removeAfterFinish(): boolean {
        return this._removeAfterFinish;
    }

    public set removeAfterFinish(v: boolean) {
        this._removeAfterFinish = v;
    }

    /**
     * Nastavení zda má být upload ze seznamu odstraněn pokud skončí chybou
     */
    public get removeAfterError(): boolean {
        return this._removeAfterError;
    }

    public set removeAfterError(v: boolean) {
        this._removeAfterError = v;
    }

    /**
     * Nastavení zda má být upload ze seznamu odstraněn pokud je zrušen uživatelem
     */
    public get removeAfterCancel(): boolean {
        return this._removeAfterCancel;
    }

    public set removeAfterCancel(v: boolean) {
        this._removeAfterCancel = v;
    }

    /**
     * Pokud je na true, provede se po dokončení uploadu UpdateInstance na daném objektu
     */
    public get updateInstanceAfterUpload(): boolean {
        return this._updateInstanceAfterUpload;
    }

    public set updateInstanceAfterUpload(v: boolean) {
        this._updateInstanceAfterUpload = v;
    }

    /**
     * Informace o tom jestli probiha nejaky upload
     */
    public get isUploading(): boolean {
        for (const u of this._uploadList) {
            const data = u["1"];
            if (data.uploader.isStarted && !data.uploader.isComplete)
                return true;
        }

        return false;
    }
    //#endregion Properties

    /**
     * Příprava dat pro budoucí upload - umožňuje naslouchat událostem a změnám uploadu, je možné volat opakovaně
     * @param instanceId instance objektu, pro kterou se má registrovat upload
     * @param attrName název atributu objektu, pro který se má registrovat upload
     * @param customData volitelná data
     * @param roles Role přidělené objektu
     */
    public registerUpload(instanceId: string, attrName: string, customData?: any, requestContext?: RequestContext): IUploadManagerInfo {
        const id = this._getUploadId(instanceId, attrName);

        // zkontroluje se, zda už data nejsou vytvořena
        let uploadData: IUploadManagerInfo = this._uploadList.get(id);
        if (uploadData != null)
            return uploadData;

        uploadData = {
            uploader: new FileUploader(),
            attrName: attrName,
            customData: customData,
            className: null,
            object: null,
            lastError: null,
        };

        this._uploadList.set(id, uploadData);

        this._registerStandardEvents(uploadData, requestContext);

        this.onUploadRegistered.trigger(this, uploadData);

        return uploadData;
    }

    /**
     * Spustí nahrávání souboru
     * @param streamReader reader umožňující číst data k nahrání
     * @param object objekt, do kterého se má nahrávání provést
     * @param attrName název atributu objektu, do kterého se má nahrávání provést
     * @param hashProvider funkce, která vrátí hash nahrávaného souboru
     * @param requestContext Viz {@link RequestContext}
     */
    public startUpload(streamReader: IFileStreamReader, object: OWObject, attrName: string, hashProvider?: () => string, requestContext?: RequestContext): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const data = this.getUploadData(object.instanceID, attrName);
            if (!data) {
                logger.error("Upload data not found - upload aborted", object.instanceID, attrName);
                return;
            }

            data.className = object.objectName;
            data.object = object;

            data.uploader.start(streamReader, () => OWManager.getDocumentUploadUrl(FileUtils.getFileExtension(streamReader.name),
                streamReader.size, object.instanceID, attrName, requestContext), hashProvider);

            data.uploader.onError.add((_sender, error) => reject(error), true);
            data.uploader.onCancel.add((_sender, error) => reject(error), true); // tady si nejsem jisty, jestli by to melo skoncit chybou nebo jakoze ok
            data.uploader.onFinished.add(() => resolve(), true);
        });
    }

    /**
     * Vygeneruje id pro interní identifikaci uploadu
     */
    private _getUploadId(instanceId: string, attrName: string): string {
        return `${instanceId}|${attrName}`; //`
    }

    /**
     * Vrátí data s informacemi o uploadu, null pokud takový ještě nebyl zaregistrován
     * @param instanceId instance objektu, pro kterou se hledají data
     * @param attrName název atributu objektu, pro který se má registrovat upload
     */
    public getUploadData(instanceId: string, attrName: string): IUploadManagerInfo {
        return this._uploadList.get(this._getUploadId(instanceId, attrName));
    }

    /**
     * Zruší a vyresetuje všechny aktivní (probíhající nebo v chybovém stavu) uploady na dané třídě.
     * Pokud je specifikováno i objectId, tak se zruší uploady jen tohoto id
     * @param className Název třídy, pro kterou se mají zrušit všechny uploady
     * @param objectId Id objektu, pro který se mají zrušit uploady - pokud je specifikováno, jinak všechny
     */
    public stopUploads(className: string, objectId: string = null): void {
        if (!className) {
            logger.error("ClassName must be specified");
            return;
        }

        for (const u of this._uploadList) {
            const data = u["1"];
            if (data.className === className && data.uploader.isActive && (!objectId || (data.object != null && data.object.id === objectId)))
                data.uploader.cancel();
        }
    }

    /**
     * Vratí true pokud na dané třídě probíhá upload
     * @param className název třídy
     */
    public isUploadRunningOnClass(className: string): boolean {
        for (const u of this._uploadList) {
            const data = u["1"];
            if (data.className === className && data.uploader.isStarted && !data.uploader.isComplete)
                return true;
        }

        return false;
    }

    private _uploadStartCounter(className?: string): void {
        logger.log("UPL-CNT-START");
        this._uploadStarted["_global"]++;
        if (className) {
            if (!this._uploadStarted[className])
                this._uploadStarted[className] = 0;
            this._uploadStarted[className]++;
        }
    }

    private _uploadCompleteCounter(className?: string): void {
        logger.log("UPL-CNT-END");
        this._uploadCompleted["_global"]++;
        if (className) {
            if (!this._uploadCompleted[className])
                this._uploadCompleted[className] = 0;
            this._uploadCompleted[className]++;
        }
    }

    private _registerStandardEvents(uploader: IUploadManagerInfo, _requestContext?: RequestContext): void {
        uploader.uploader.onError.add((_item, _v) => {
            this._uploadCompleteCounter(uploader.className);
            this.onUploadError.trigger(this, uploader);
            if (this.removeAfterError)
                this.removeUploadData(uploader);
        });

        uploader.uploader.onProgress.add((_item, _v) => {
            this.onUploadProgress.trigger(this, uploader);
        });

        uploader.uploader.onCancel.add((_item, _v) => {
            this._uploadCompleteCounter(uploader.className);
            this.onUploadCancel.trigger(this, uploader);
            if (this.removeAfterCancel)
                this.removeUploadData(uploader);
        });

        uploader.uploader.onFinished.add((_item, _v) => {
            this._uploadCompleteCounter(uploader.className);
            if (this.updateInstanceAfterUpload) {
                const data = SerializeFuncs.deserializeOWDocument(uploader.uploader.documentId, uploader.object.instanceID, uploader.attrName);
                uploader.object.setValue(uploader.attrName, data);
            }
            this.onUploadComplete.trigger(this, uploader);
            if (this.removeAfterFinish)
                this.removeUploadData(uploader);
        });

        uploader.uploader.onStart.add((_item, _v) => {
            this._uploadStartCounter(uploader.className);
            this.onUploadStart.trigger(this, uploader);
        });
    }

    public removeUploadData(uploadData: IUploadManagerInfo): void {
        if (uploadData == null)
            return;

        const id = this._getUploadId(uploadData.object.instanceID, uploadData.attrName);
        uploadData.uploader.cancel();
        this._uploadList.delete(id);
    }
}
